import { combine, createEffect, createEvent, createStore, sample } from 'effector';
import { debounce } from 'patronum';
import { api, unwrap, type Schemas } from '../../shared/api/client';
import { latest } from '../../shared/lib/latest';

export type Position = Schemas['PositionOut'];
export type PositionDetail = Schemas['PositionDetailOut'];

export const positionsRefreshed = createEvent();
export const positionSelected = createEvent<number | null>();
export const detailRequested = createEvent<number>();

export const loadPositionsFx = createEffect(() =>
  unwrap(api.GET('/api/v1/positions', { params: { query: { scope: 'active' } } })),
);

export const loadDetailFx = createEffect((id: number) =>
  unwrap(api.GET('/api/v1/positions/{position_id}', { params: { path: { position_id: id } } })),
);

export const openPositionFx = createEffect((body: Schemas['PositionIn']) =>
  unwrap(api.POST('/api/v1/positions', { body })),
);

export const recordFillFx = createEffect(({ id, body }: { id: number; body: Schemas['FillIn'] }) =>
  unwrap(
    api.POST('/api/v1/positions/{position_id}/fills', {
      params: { path: { position_id: id } },
      body,
    }),
  ),
);

export const deleteFillFx = createEffect(({ id, fillId }: { id: number; fillId: number }) =>
  unwrap(
    api.DELETE('/api/v1/positions/{position_id}/fills/{fill_id}', {
      params: { path: { position_id: id, fill_id: fillId } },
    }),
  ),
);

export const skipLevelFx = createEffect(({ id, n }: { id: number; n: number }) =>
  unwrap(
    api.POST('/api/v1/positions/{position_id}/levels/{n}/skip', {
      params: { path: { position_id: id, n } },
    }),
  ),
);

export const closePositionFx = createEffect(
  ({ id, body }: { id: number; body: Schemas['CloseIn'] }) =>
    unwrap(
      api.POST('/api/v1/positions/{position_id}/close', {
        params: { path: { position_id: id } },
        body,
      }),
    ),
);

export const cancelPositionFx = createEffect((id: number) =>
  unwrap(
    api.POST('/api/v1/positions/{position_id}/cancel', { params: { path: { position_id: id } } }),
  ),
);

export const updatePositionFx = createEffect(
  ({ id, patch }: { id: number; patch: Schemas['PositionPatch'] }) =>
    unwrap(
      api.PATCH('/api/v1/positions/{position_id}', {
        params: { path: { position_id: id } },
        body: patch,
      }),
    ),
);

/** Any successful change returns the updated position. */
export const positionChanged = createEvent<Position>();

sample({
  clock: [
    openPositionFx.doneData,
    recordFillFx.doneData,
    deleteFillFx.doneData,
    skipLevelFx.doneData,
    closePositionFx.doneData,
    cancelPositionFx.doneData,
    updatePositionFx.doneData,
  ],
  target: positionChanged,
});

/** Reloads asked for at the same moment (a change, its alerts, a live event) are merged. */
const COALESCE_MS = 50;

// Only the latest load's answer counts: a slow answer for the previous selection never replaces
// the current one.
const positions = latest(loadPositionsFx);
const detail = latest(loadDetailFx);

export const $positions = createStore<Position[] | null>(null).on(
  positions.done,
  (_, list) => list,
);

export const $selectedId = createStore<number | null>(null)
  .on(positionSelected, (_, id) => id)
  .on(openPositionFx.doneData, (_, created) => created.id);

export const $detail = createStore<PositionDetail | null>(null)
  .on(detail.done, (_, result) => result)
  .on(detail.fail, () => null);

export const $detailFailed = createStore(false)
  .on(detail.fail, () => true)
  .on(detail.done, () => false);

export const $selected = combine(
  $positions,
  $selectedId,
  (list, id) => list?.find((p) => p.id === id) ?? null,
);

const positionsWanted = createEvent();
sample({ clock: [positionsRefreshed, positionChanged], target: positionsWanted });
sample({ clock: debounce(positionsWanted, COALESCE_MS), target: positions.run });

// The detail follows the selection and refreshes after changes.
const detailWanted = createEvent<number>();
// A new request clears an earlier "not found" at once, not after the merge delay.
$detailFailed.reset(detailWanted);
sample({ clock: detailRequested, target: detailWanted });
sample({
  clock: [$selectedId.updates, positionChanged, positionsRefreshed],
  source: $selectedId,
  filter: (id): id is number => id !== null,
  target: detailWanted,
});
sample({ clock: debounce(detailWanted, COALESCE_MS), target: detail.run });
